import React, { useCallback, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Typography,
  useTheme,
} from "@mui/material";
import {
  OutlineDocumentText,
  Paperclip,
  TrashCan,
} from "../../../../../../Icon";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useTranslation } from "react-i18next";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import { useSnackbar } from "notistack";
import { createAttachment, updateInterview } from "../../../../../../../apis";
import EGFileUpload from "../../../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";

export const AddConsentFiles = ({
  id,
  show,
  alias,
  onClose,
  attachments,
  setInterviews,
  typeFileOpts,
}) => {
  // Custom Hooks
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  // Initial States
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);

  // ---- Utilities ---- //
  const normalizeFiles = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((entry) => {
      const isWrapped = entry && typeof entry === "object" && "file" in entry;
      const fileEntry = isWrapped ? entry : { file: entry };
      return {
        ...fileEntry,
        purpose: fileEntry.purpose ?? undefined,
        // By default, we do not mark it as invalid until the user attempts to save.
        isValid: fileEntry.isValid ?? false,
      };
    });
  }, []);

  const handleSetFilesFromUpload = useCallback(
    (incoming) => {
      if (typeof incoming === "function") {
        setFiles((prev) => {
          const result = incoming(prev);
          return normalizeFiles(result);
        });
      } else {
        setFiles(normalizeFiles(incoming));
      }
    },
    [normalizeFiles]
  );

  // --- Generate a unique key ---
  const getEntryKey = (entry) => {
    const f = extractFile(entry);
    return `${f?.name ?? "unknown"}-${f?.lastModified ?? ""}`;
  };

  // For very long titles, show the desired length.
  const fileTitle = (fileName, maxLength = 25, showString = 25) => {
    if (fileName?.length > maxLength) {
      const firstPt = fileName.substring(0, showString);
      return `${firstPt}...`;
    }
    return fileName;
  };

  const extractFile = (entry) => (entry && entry.file ? entry.file : entry);
  // Format size
  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";

    const KB = 1024;
    const MB = 1024 * KB;

    if (bytes >= MB) {
      return `${(bytes / MB).toFixed(1)} MB`;
    } else {
      return `${Math.round(bytes / KB)} KB`;
    }
  };

  // Remove file before sending
  const removeFile = useCallback((uniqueKey) => {
    setFiles((prev) =>
      prev.filter((entry) => getEntryKey(entry) !== uniqueKey)
    );
  }, []);

  // Update purpose by key (string)
  const updateFilePurpose = useCallback((uniqueKey, value) => {
    setFiles((prev) =>
      prev.map((entry) => {
        const key = getEntryKey(entry);
        if (key === uniqueKey) {
          return {
            ...entry,
            purpose: value,
            isValid: !value,
          };
        }
        return entry;
      })
    );
  }, []);

  // Close & clean modal
  const handleClose = () => {
    setFiles([]);
    onClose();
  };
  // Send data
  const handleSave = async () => {
    // Detect files without purpose
    const missing = files.filter((f) => !f.purpose);

    if (missing.length > 0) {
      enqueueSnackbar(
        t("Interview.select_a_classification_for_each_file_before_saving"),
        { variant: "error" }
      );
      // Mark as invalid those that have no purpose
      setFiles((prev) =>
        prev.map((entry) => {
          return {
            ...entry,
            isValid: !entry.purpose,
          };
        })
      );
      return; // exit without sending
    }

    const saving = enqueueSnackbar(`${t("Interview.saving")}...`, {
      variant: "info",
      persist: true,
    });
    setIsLoading(true);

    try {
      const uploadPromises = files.map(async (entry) => {
        const file = extractFile(entry);
        const fd =
          entry && entry.formData
            ? entry.formData
            : (() => {
                const newFd = new FormData();
                newFd.append("attachments", file);
                return newFd;
              })();
        const resp = await createAttachment(fd);
        const fileId = resp.attachments_id;
        return {
          attachments_file: fileId,
          attachments_data: {
            name: file.name,
            size: file.size,
            type: file.type,
            purpose: entry.purpose,
          },
        };
      });
      const settled = await Promise.allSettled(uploadPromises);
      const successes = settled
        .filter((s) => s.status === "fulfilled")
        .map((s) => s.value);
      const failures = settled.filter((s) => s.status === "rejected");
      if (successes.length === 0) {
        throw new Error("No file could be uploaded.");
      }
      const existing =
        Array.isArray(attachments) && attachments.length
          ? attachments.map((e) => ({
              attachments_file: e.attachments_file,
              attachments_data: e.attachments_data,
            }))
          : [];

      const combined = [...existing, ...successes].reduce((acc, item) => {
        if (!acc.find((i) => i.attachments_file === item.attachments_file)) {
          acc.push(item);
        }
        return acc;
      }, []);

      const dataToSend = {
        alias,
        interviews_id: id,
        interviews_data: { attachments: combined },
      };

      const resp = await updateInterview(dataToSend);
      setInterviews(resp.data.interviews);
      handleClose();
      if (failures.length > 0) {
        enqueueSnackbar(
          `${successes.length} ${t("Interview.files_uploaded_successfully")}. ${
            failures.length
          } ${t("Interview.failed")}.`,
          { variant: "warning" }
        );
      } else {
        enqueueSnackbar(t("Interview.data_saved_successfully"), {
          variant: "success",
        });
      }
    } catch (err) {
      console.error("Error uploading files: ", err);
      enqueueSnackbar(t("Interview.error_uploading_files"), {
        variant: "error",
      });
    } finally {
      closeSnackbar(saving);
      setIsLoading(false);
    }
  };
  // Open preview template modal
  const handleOpenTemplatePreview = (data) => {
    setCurrentTemplate(data);
    setOpenTemplate(true);
  };
  
  // Close preview template modal
  const handleCloseTemplate = () => {
    setOpenTemplate(false);
    setCurrentTemplate(null);
  };

  const templates = [
    {
      title: t("Interview.interview_consent"),
      src: "https://www.ethicsglobal.com/docs/es/Consentimiento_Entrevista_Investigacion.docx",
    },
    {
      title: t("Interview.consent_to_record_interview"),
      src: "https://www.ethicsglobal.com/docs/es/Consentimiento_Grabacion_Entrevista.docx",
    },
  ];

  return (
    <>
      <Dialog open={show} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.blueGray50?.main}`,
          }}
        >
          <Typography component="span" fontWeight={600} fontSize={20}>
            {t("Interview.attach_consent_documents")}
          </Typography>

          <EGIconButton
            iconName="cancel_circle"
            iconColor="primary"
            onClick={handleClose}
            disabled={isLoading}
            sx={{ position: "absolute", top: 15, right: 12 }}
            iconSize={25}
          />
        </DialogTitle>

        <DialogContent>
          <Box px={1}>
            <Box mt={1} mb={2}>
              <Typography color="textSecondary" variant="subtitle1" mb={0.5}>
                {t("Interview.consent_form_templates")}:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1,
                  px: 0.5,
                  pb: 1.5,
                  borderBottom: `1px solid ${theme.palette.blueGray100.main}`,
                  alignItems: "stretch",
                }}
              >
                {templates.map((template, idx) => (
                  <ListItem
                    key={idx}
                    secondaryAction={
                      <EGIconButton
                        iconName="outline_import"
                        title={t("Interview.download")}
                        href={template.src}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Download ${template.title}`}
                      />
                    }
                    disablePadding
                  >
                    <ListItemButton
                      role={undefined}
                      dense
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 4,
                        height: "100%",
                      }}
                      onClick={() => handleOpenTemplatePreview(template)}
                    >
                      <ListItemIcon
                        sx={{
                          border: `1px solid ${theme.palette.blueGray50.main}`,
                          borderRadius: "50%",
                          minWidth: "auto",
                          p: 0.5,
                          mr: 1,
                          bgcolor: theme.palette.background.paper,
                        }}
                      >
                        <OutlineDocumentText
                          color={theme.palette.primary.dark}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={template.title}
                        primaryTypographyProps={{ textAlign: "center" }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>
            <EGFileUpload
              title={t("Interview.consent_files")}
              customIcons={["outline_export"]}
              files={files}
              setFiles={handleSetFilesFromUpload}
              disabled={isLoading}
            />

            {/* List of files */}
            <Stack
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                },
                gap: 1,
                mt: files.length > 0 ? 1.5 : 0,
                p: 0.5,
                maxHeight: "25vh",
                overflowY: "auto",
                width: "100%",
              }}
            >
              {files.map((entry) => {
                const file = extractFile(entry);
                const entryKey = getEntryKey(entry);

                return (
                  <Chip
                    key={entryKey}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexFlow: "column nowrap",
                          gap: 0.5,
                          width: "100%",
                          py: 0.3,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontStyle={"italic"}
                            color="secondary"
                          >
                            <Paperclip
                              size={14}
                              color={theme.palette.secondary.main}
                            />
                            {`${" "} ${fileTitle(file.name)} (${formatFileSize(
                              file.size
                            )})`}
                          </Typography>
                        </Box>
                        <EGBasicSelect
                          label={`${t("Interview.document_classification")}:`}
                          value={entry.purpose ?? ""}
                          onChange={(e) =>
                            updateFilePurpose(entryKey, e.target.value)
                          }
                          options={typeFileOpts}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ minWidth: 110, width: "100%" }}
                          isValid={!entry.isValid}
                        />
                      </Box>
                    }
                    variant="outlined"
                    sx={{
                      boxShadow: `0 0 5px -2px ${theme.palette.common.black}`,
                      border: "transparent",
                      display: "flex",
                      width: "100%",
                      "& .MuiChip-label": {
                        width: "100%",
                      },
                      fontSize: 13,
                      p: 0.75,
                      height: "auto",
                      justifyContent: "space-between",
                      overflow: "hidden",
                      "& .MuiChip-deleteIcon": {
                        color: theme.palette.pink.main,
                        "&:hover": {
                          color: "error.light",
                        },
                      },
                    }}
                    onDelete={() => removeFile(entryKey)}
                    deleteIcon={
                      <SvgIcon>
                        <TrashCan />
                      </SvgIcon>
                    }
                  />
                );
              })}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ pb: 2, px: 2 }}>
          <EGButton
            label={t("Interview.save")}
            color="success"
            labelColor="white"
            loading={isLoading}
            disabled={!files.length}
            onClick={handleSave}
          />

          <EGButton
            label={t("Interview.cancel")}
            color="secondary"
            onClick={handleClose}
            loading={isLoading}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={openTemplate} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.blueGray50?.main}`,
          }}
        >
          <Typography component="span" fontWeight={600} fontSize={20}>
            {currentTemplate?.title || ""}
          </Typography>
          <EGIconButton
            iconName="cancel_circle"
            iconColor="primary"
            onClick={handleCloseTemplate}
            disabled={isLoading}
            sx={{ position: "absolute", top: 15, right: 12 }}
            iconSize={25}
          />
        </DialogTitle>
        <DialogContent>
          {currentTemplate ? (
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${currentTemplate?.src}`}
              style={{
                width: "100%",
                height: "80vh",
                border: "none",
              }}
            />
          ) : (
            <Typography color="error">
              {t("Interview.no_file_available_for_preview")}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
